import React, { useCallback, useContext, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { graphql } from "react-relay";
import TextArea from "react-textarea-autosize";

import { faClock } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBullseyeArrow, faLevelUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowUp, faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";
import moment, { type Moment } from "moment";
import { Loader } from "semantic-ui-react";

import { DueDate } from "~/common";
import type { TeamType, UserType } from "~/common/AssigneeFilter";
import ViewportContext from "~/common/ViewportContext";
import { commitMutationPromise } from "~/lib/relayUtil";
import { isDesktopApp, isTeams } from "~/lib/utils";
import { environment } from "~/models";
import { DueDateSelect } from "~/notes/AssignableDropdown";
import AssigneeSelector from "~/okrs/components/AssigneeSelector";
import CollapsibleDescription from "~/okrs/components/CollapsibleDescription";
import StatusInput from "~/okrs/components/StatusInput";
import WeightInput from "~/okrs/components/WeightInput";
import EditObjective from "~/okrs/EditObjective";
import KeyResult from "~/okrs/KeyResult";
import { OKRsContext, type OKRsContextType } from "~/okrs/ObjectiveList";
import ObjectiveQuery from "~/okrs/ObjectiveQuery";
import { Button } from "~/ui";
import Popup from "~/ui/legacy/Popup";
import { IconButton } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type {
	ObjectiveCreateOrEditMutation,
	ObjectiveInput,
} from "~/__generated__/ObjectiveCreateOrEditMutation.graphql";
import type {
	ObjectiveQuery_Info$data as ObjectiveQueryInfo,
	ObjectiveStatus,
} from "~/__generated__/ObjectiveQuery_Info.graphql";

import stylesObjectiveList from "~/okrs/ObjectiveList.module.css";

import styles from "./Objective.module.css";

export type ObjectiveType = Omit<ObjectiveQueryInfo, " $fragmentType">;

export type ObjectiveAttributes = ObjectiveType & {
	children?: ObjectiveType[];
};

type ObjectiveProps = {
	onTitleChange?: (newTitle: string) => void;
	onDescriptionChange?: (newDescription: string) => void;
	onDueDateChange?: (newDueDate: Moment | null) => void;
	onAssigneeChange?: (newUser: UserType) => void;
	onAssigneeTeamChange?: (newTeam: TeamType) => void;
	onStatusChange?: (newStatus: ObjectiveStatus) => void;
	onWeightChange?: (newWeight: number) => void;
	hasError?: boolean;
} & ObjectiveAttributes;
export const objectiveCreateOrEditMutation = graphql`
	mutation ObjectiveCreateOrEditMutation($objective: ObjectiveInput!) {
		Objective_CreateEdit(objective: $objective) {
			objective {
				id
				title
				visibility
				dueDate
				assigneeUser {
					id
					email
					image
					fullName
					isGuest
				}
				assigneeTeam {
					id
					title
				}
				contributors {
					id
					email
					image
					fullName
					isGuest
				}
				contributorsTeams {
					id
					title
				}
				description
				status
				archived
				weight
				visibility
				cycle {
					id
				}
				parent {
					id
					title
					level
					cycle {
						startDate
						endDate
					}
				}
				progress

				keyResults {
					id
					title
					dueDate
					assigneeUser {
						id
						email
						image
						fullName
						isGuest
					}
					assigneeTeam {
						id
						title
					}
					metricProgress
					metricType
					metricCurrency
					metricStart
					metricCurrent
					metricTarget
					weight
					order
					progress
				}
				hasAccess
			}
		}
	}
`;

export enum Visibility {
	PUBLIC = "PUBLIC",
	PRIVATE = "PRIVATE",
}

export default function Objective({
	id,
	title,
	dueDate,
	assigneeUser,
	assigneeTeam,
	status,
	archived,
	weight,
	visibility,
	contributors,
	contributorsTeams,
	keyResults,
	children,
	description,
	cycle,
	parent,
	progress,
	hasAccess,
	onTitleChange: parentOnTitleChange,
	onDescriptionChange: parentOnDescriptionChange,
	onDueDateChange: parentOnDueDateChange,
	onAssigneeChange: parentOnAssigneeChange,
	onAssigneeTeamChange: parentOnAssigneeTeamChange,
	onStatusChange: parentOnStatusChange,
	onWeightChange: parentOnWeightChange,
	hasError,
}: ObjectiveProps) {
	const {
		createView,
		editView,
		refetch,
		filters,
		children: child,
		team,
		setIsLoading,
	} = useContext<OKRsContextType>(OKRsContext);
	const [collapsed, setCollapsed] = useState<boolean>(true);
	const [dayPickerOpened, setDayPickerOpened] = useState<boolean>(false);
	const { isMobile } = useContext(ViewportContext);

	const stopPropagation = useCallback((event: React.MouseEvent) => {
		event.stopPropagation();
	}, []);

	const toggleCollapsed = (event: React.MouseEvent) => {
		stopPropagation(event);
		setCollapsed(!collapsed);
	};

	const openDayPicker = useCallback(
		(event: React.MouseEvent) => {
			stopPropagation(event);
			setDayPickerOpened(true);
		},
		[stopPropagation],
	);

	const closeDayPicker = useCallback(() => {
		setDayPickerOpened(false);
	}, []);

	const onEditObjective = useCallback(
		(editedFields: Omit<Partial<ObjectiveInput>, "id" | "progress">) => {
			setIsLoading?.(true);
			commitMutationPromise<ObjectiveCreateOrEditMutation>(environment, {
				mutation: objectiveCreateOrEditMutation,
				variables: {
					objective: {
						id,
						...editedFields,
					},
				},
				optimisticUpdater: store => {
					const node = store.get(id);
					if (!node) {
						return;
					}
					for (const [key, value] of Object.entries(editedFields)) {
						if (["assigneeTeam", "assigneeUser"].includes(key) && value) {
							const assigneeNode = store.get(value as string);
							if (assigneeNode) node.setLinkedRecord(assigneeNode, key);
						} else {
							// @ts-ignore
							node.setValue(value, key);
						}
					}
				},
				onCompleted: () => {
					setIsLoading?.(false);
				},
				onError: e => {
					setIsLoading?.(false);
					// TODO: Show error somewhere
					console.log(e);
				},
			});
		},
		[id, setIsLoading],
	);

	const debouncedOnEditObjective = useMemo(() => debounce(onEditObjective, 500), [onEditObjective]);

	const onTitleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (!event.target) {
				return;
			}
			parentOnTitleChange?.(event.target.value);
			if (!id) {
				return;
			}
			debouncedOnEditObjective({ title: event.target.value });
		},
		[parentOnTitleChange, id, debouncedOnEditObjective],
	);

	const onDescriptionChange = useCallback(
		(newDescription: string) => {
			parentOnDescriptionChange?.(newDescription);
			if (!id) {
				return;
			}
			debouncedOnEditObjective({ description: newDescription });
		},
		[parentOnDescriptionChange, id, debouncedOnEditObjective],
	);

	const onDescriptionChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (!event.target) {
				return;
			}
			onDescriptionChange(event.target.value);
		},
		[onDescriptionChange],
	);

	const onDueDateChange = useCallback(
		(newDueDate: Moment) => {
			closeDayPicker();
			parentOnDueDateChange?.(newDueDate);
			if (!id) {
				return;
			}
			onEditObjective({ dueDate: newDueDate.format("YYYY-MM-DD") });
		},
		[closeDayPicker, parentOnDueDateChange, id, onEditObjective],
	);

	const onDueDateRemoved = useCallback(() => {
		closeDayPicker();
		parentOnDueDateChange?.(null);
		if (!id) {
			return;
		}
		onEditObjective({ dueDate: null });
	}, [closeDayPicker, parentOnDueDateChange, id, onEditObjective]);

	const onDatePickerInsideClick = useCallback((event: React.MouseEvent) => {
		event.preventDefault();
	}, []);

	const onStatusChange = useCallback(
		(newStatus: ObjectiveStatus) => {
			parentOnStatusChange?.(newStatus);
			if (!id) {
				return;
			}
			onEditObjective({ status: newStatus });
		},
		[parentOnStatusChange, id, onEditObjective],
	);

	const onAssigneeChange = useCallback(
		(newAssignee: UserType) => {
			parentOnAssigneeChange?.(newAssignee);
			if (!id) {
				return;
			}
			onEditObjective({ assigneeUser: newAssignee.id, assigneeTeam: null });
		},
		[parentOnAssigneeChange, id, onEditObjective],
	);

	const onAssigneeTeamChange = useCallback(
		(newAssigneeTeam: TeamType) => {
			parentOnAssigneeTeamChange?.(newAssigneeTeam);
			if (!id) {
				return;
			}
			onEditObjective({ assigneeTeam: newAssigneeTeam.id, assigneeUser: null });
		},
		[parentOnAssigneeTeamChange, id, onEditObjective],
	);

	const onWeightChange = useCallback(
		(newWeight: number) => {
			parentOnWeightChange?.(newWeight);
			if (!id) {
				return;
			}
			onEditObjective({ weight: newWeight });
		},
		[parentOnWeightChange, id, onEditObjective],
	);

	const openParentObjective = useCallback(
		(event: React.MouseEvent) => {
			if (!parent) {
				return;
			}
			event.preventDefault();
			const target = isDesktopApp || isTeams ? "_self" : "_blank";
			window.open(`${window.location.origin}/objectives/${parent.id}/`, target);
		},
		[parent],
	);

	const onEditClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.defaultPrevented) return;

			event.preventDefault();

			__globalModalContext?.open(({ close }) => (
				<EditObjective
					onClose={close}
					objective={{
						id,
						title,
						dueDate,
						assigneeUser,
						assigneeTeam,
						status,
						archived,
						weight,
						visibility,
						contributors,
						contributorsTeams,
						keyResults,
						children,
						description,
						cycle,
						parent,
						progress,
						hasAccess,
					}}
					refetch={refetch}
					fromTeam={team}
				/>
			));
		},
		[
			id,
			title,
			dueDate,
			assigneeUser,
			assigneeTeam,
			status,
			archived,
			weight,
			visibility,
			contributors,
			contributorsTeams,
			keyResults,
			children,
			description,
			cycle,
			parent,
			progress,
			hasAccess,
			refetch,
			team,
		],
	);

	let progressClass = styles.progressGrey;
	if ((!createView && !editView) || id) {
		progressClass = styles.progressGreen;
		if (progress <= 30) {
			progressClass = styles.progressOrange;
		} else if (progress <= 60) {
			progressClass = styles.progressYellow;
		}
	}

	if (createView || editView || child) {
		return (
			<article
				className={cx(styles.container, styles.createOrEditView, {
					[styles.archived]: archived,
					[styles.childView]: child,
					[styles.createView]: createView,
					[styles.error]: hasError,
				})}
			>
				<TextArea
					autoFocus
					value={title || ""}
					onChange={onTitleChange}
					placeholder="Objective title"
					className={cx(styles.title, stylesObjectiveList.title)}
					disabled={!hasAccess || child}
				/>
				{!child && (
					<TextArea
						value={description || ""}
						minRows={1}
						onChange={onDescriptionChangeEvent}
						placeholder="Add a description"
						className={styles.description}
						disabled={!hasAccess}
					/>
				)}
				<div
					className={cx(styles.objectiveData, {
						[styles.archived]: archived,
					})}
				>
					<div className={cx(styles.assignedTo, stylesObjectiveList.assignedTo)}>
						<AssigneeSelector
							user={assigneeUser}
							team={assigneeTeam}
							setUser={onAssigneeChange}
							setTeam={onAssigneeTeamChange}
							readOnly={!hasAccess || child}
							avatarOnly={isMobile}
							forceCloseOnOutsideClick
						/>
					</div>
					<div className={cx(styles.dueDate, stylesObjectiveList.dueDate)}>
						{dueDate ? (
							<DueDate
								as={Button}
								short
								done={progress >= 100}
								date={dueDate}
								className={cx("time", styles.dueDateTime, styles.dataButton, {
									[styles.dueDateDone]: progress >= 100,
									[styles.dataButtonDisabled]: !hasAccess || child,
								})}
								onClick={openDayPicker}
							/>
						) : (
							<IconButton
								icon={faClock}
								className={cx(styles.dueDateClock, styles.dataButton, {
									[styles.dataButtonDisabled]: !hasAccess || child,
								})}
								onClick={openDayPicker}
							/>
						)}
						{dayPickerOpened && (
							<OutsideClickHandler onOutsideClick={closeDayPicker}>
								<div className={cx("date-picker", styles.datePicker)} onClickCapture={onDatePickerInsideClick}>
									<DueDateSelect
										date={dueDate ? moment(dueDate) : undefined}
										updateDate={onDueDateChange}
										removeDate={onDueDateRemoved}
										goBack={closeDayPicker}
									/>
								</div>
							</OutsideClickHandler>
						)}
					</div>
					<div className={cx(styles.status, stylesObjectiveList.status)}>
						<StatusInput
							selected={status}
							onChange={onStatusChange}
							className={cx(styles.dataButton, { [styles.dataButtonDisabled]: !hasAccess || child })}
						/>
					</div>
					{child ? (
						<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass, styles.childProgress)}>
							<WeightInput
								weight={weight}
								onChange={onWeightChange}
								className={styles.weightSelection}
								disabled={!hasAccess}
							/>
							{progress}%
						</div>
					) : parent && weight > 0 ? (
						<Popup
							inverted
							position="top center"
							size="tiny"
							trigger={
								<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass)}>
									<FontAwesomeIcon icon={faArrowUp} />
									{progress}%
								</div>
							}
							content={"Contributes to parent percentage"}
						/>
					) : (
						<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass)}>{progress}%</div>
					)}
				</div>
			</article>
		);
	}

	if (isMobile) {
		return (
			<article
				className={cx(styles.containerWrapper, styles.container, styles.objectiveData, { [styles.archived]: archived })}
				onClick={!editView || child ? onEditClick : undefined}
			>
				<div>
					<div className={cx(styles.title, stylesObjectiveList.title)}>
						<FontAwesomeIcon icon={faBullseyeArrow} />
						{title || "Untitled Objective"}
					</div>
					{dueDate && (
						<DueDate
							short
							done={progress >= 100}
							date={dueDate}
							className={cx("time", styles.dueDate, stylesObjectiveList.dueDate, styles.dataButtonDisabled, {
								[styles.dueDateDone]: progress >= 100,
							})}
							onClick={openDayPicker}
						/>
					)}
				</div>
				<div>
					<div className={cx(styles.assignedTo, stylesObjectiveList.assignedTo)}>
						<AssigneeSelector
							user={assigneeUser}
							team={assigneeTeam}
							setUser={onAssigneeChange}
							setTeam={onAssigneeTeamChange}
							readOnly
						/>
					</div>
					<div className={styles.badges}>
						<div className={cx(styles.status, stylesObjectiveList.status)}>
							<StatusInput
								selected={status}
								onChange={onStatusChange}
								className={cx(styles.dataButton, styles.dataButtonDisabled)}
							/>
						</div>
						<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass)}>{progress}%</div>
					</div>
				</div>
			</article>
		);
	}

	return (
		<article
			className={cx(styles.containerWrapper, {
				[styles.archived]: archived,
				[styles.error]: hasError,
			})}
			onClick={onEditClick}
		>
			<div className={styles.container}>
				<div
					className={cx(styles.objectiveData, {
						[styles.archived]: archived,
						[styles.expanded]: !collapsed,
					})}
				>
					<div className={cx(styles.title, stylesObjectiveList.title)}>
						<button
							className={cx(styles.collapseButton, {
								[styles.collapseButtonCollapsed]: collapsed,
								[styles.untitled]: !title,
							})}
							onClick={toggleCollapsed}
						>
							<FontAwesomeIcon icon={faChevronDown} className={styles.collapseButtonCaret} />
						</button>
						<FontAwesomeIcon icon={faBullseyeArrow} />
						<span title={title || "Untitled Objective"}>{title || "Untitled Objective"}</span>
					</div>
					<div className={cx(styles.dueDate, stylesObjectiveList.dueDate)}>
						{dueDate ? (
							<DueDate
								as={Button}
								short
								done={progress >= 100}
								date={dueDate}
								className={cx("time", styles.dueDateTime, styles.dataButton, {
									[styles.dueDateDone]: progress >= 100,
									[styles.dataButtonDisabled]: !hasAccess,
								})}
								onClick={openDayPicker}
							/>
						) : (
							<IconButton
								icon={faClock}
								className={cx(styles.dueDateClock, styles.dataButton, { [styles.dataButtonDisabled]: !hasAccess })}
								onClick={openDayPicker}
							/>
						)}
						{dayPickerOpened && (
							<OutsideClickHandler onOutsideClick={closeDayPicker}>
								<div className={cx("date-picker", styles.datePicker)} onClickCapture={onDatePickerInsideClick}>
									<DueDateSelect
										date={dueDate ? moment(dueDate) : undefined}
										updateDate={onDueDateChange}
										removeDate={onDueDateRemoved}
										goBack={closeDayPicker}
									/>
								</div>
							</OutsideClickHandler>
						)}
					</div>
					<div className={cx(styles.assignedTo, stylesObjectiveList.assignedTo)} onClick={stopPropagation}>
						<AssigneeSelector
							user={assigneeUser}
							team={assigneeTeam}
							setUser={onAssigneeChange}
							setTeam={onAssigneeTeamChange}
							readOnly={!hasAccess}
						/>
					</div>
					<div className={cx(styles.status, stylesObjectiveList.status)} onClick={stopPropagation}>
						<StatusInput
							selected={status}
							onChange={onStatusChange}
							className={cx(styles.dataButton, { [styles.dataButtonDisabled]: !hasAccess })}
						/>
					</div>
					{parent && weight > 0 ? (
						<Popup
							inverted
							position="top center"
							size="tiny"
							trigger={
								<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass)}>
									<FontAwesomeIcon icon={faArrowUp} />
									{progress}%
								</div>
							}
							content={"Contributes to parent percentage"}
						/>
					) : (
						<div className={cx(styles.progress, stylesObjectiveList.progress, progressClass)}>{progress}%</div>
					)}
					<div className={cx(styles.goToParent, stylesObjectiveList.goToParent)}>
						{parent && (
							<Popup
								inverted
								position="top center"
								size="tiny"
								trigger={
									<IconButton icon={faLevelUp} className={styles.upArrowProgress} onClick={openParentObjective} />
								}
								content={"Go to parent objective"}
							/>
						)}
					</div>
				</div>
				{!collapsed && (
					<div>
						<div className={styles.keyResults}>
							{description && <CollapsibleDescription description={description} />}
							{keyResults.map(keyResult => (
								<KeyResult
									key={keyResult.id}
									{...keyResult}
									objectiveId={id}
									hasAccess={hasAccess}
									archived={archived}
								/>
							))}
						</div>
					</div>
				)}
			</div>

			{!collapsed && filters?.view !== "LIST" && (
				<div className={styles.childObjectives}>
					{children && children.map(objective => <Objective key={objective.id} {...objective} />)}

					{!children && filters?.view === "TREE" && (
						<React.Suspense fallback={<Loader active size="tiny" inline className={styles.childrenLoading} />}>
							<ObjectiveQuery parent={id} />
						</React.Suspense>
					)}
				</div>
			)}
		</article>
	);
}
